import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountsConfigurationKeys,
  AccountsConfigurations,
  ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES,
  ACCOUNTS_FEATURE_NAME,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  selectIsBeSearchEnabled,
  selectNoSearchResult,
  setSearchQuery,
} from '@epicuro-next/state/accounts-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

@Component({
  selector: 'epc-search-accounts-input',
  templateUrl: './search-accounts-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchAccountsInputComponent implements OnInit {
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  private searchInputDelayRule$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        AccountsConfigurationKeys.SearchInputDelay,
        AccountsConfigurations
      >(
        ACCOUNTS_FEATURE_NAME,
        AccountsConfigurationKeys.SearchInputDelay,
        ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES[
          AccountsConfigurationKeys.SearchInputDelay
        ],
      ),
    ),
  );

  public queryChanges$ = new Subject<string>();

  public isBESearchEnabled$ = this.store.pipe(
    select(selectIsBeSearchEnabled),
    filter(isNonNullish),
  );

  public searchDebounce$ = combineLatest([
    this.searchInputDelayRule$.pipe(filter(isNonNullish)),
    this.isBESearchEnabled$,
  ]).pipe(
    map(([ruleDelay, isBESearchEnabled]) => {
      return isBESearchEnabled ? ruleDelay : 400;
    }),
  );

  public noResult$ = combineLatest([
    this.store.pipe(select(selectNoSearchResult)),
    this.isBESearchEnabled$,
  ]).pipe(
    map(([noResult, isBESearchEnabled]) => noResult && !isBESearchEnabled),
  );

  public ngOnInit() {
    this.queryChanges$
      .pipe(
        map((v) => v.trim()),
        distinctUntilChanged(),
        tap((query) => {
          this.store.dispatch(
            setSearchQuery({
              query,
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
